import type { MetaFunction } from 'react-router'
import { Link } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { FloatingCTA } from '~/components/floating-cta'
import { InlineLeadCapture } from '~/components/inline-lead-capture'
import { TrustStrip } from '~/components/trust-strip'
import { BreadcrumbSchema, FAQSchema } from '~/components/structured-data'
import { ArticleSchema } from '~/components/seo-content'

const RE_FAQS = [
  { question: "Par quels travaux commencer une rénovation énergétique ?", answer: "L'isolation des combles perdus est le geste le plus rentable du logement : 30 % d'économies de chauffage pour 20 à 35 €/m² posé, retour sur investissement en 5 à 8 ans. Vient ensuite le remplacement du système de chauffage (PAC air/eau pour 8 000-15 000 €), puis l'isolation des murs ou des fenêtres. Pour les passoires thermiques (DPE F ou G), un audit énergétique est obligatoire et oriente vers le parcours accompagné qui finance les rénovations globales." },
  { question: "Quel budget prévoir pour une rénovation énergétique complète ?", answer: "Une rénovation énergétique globale visant un gain de 2 classes DPE coûte 25 000 à 50 000 € selon la surface et l'état initial. Les aides cumulées (MaPrimeRénov' parcours accompagné + CEE + éco-PTZ) peuvent couvrir 30 à 80 % du coût pour les ménages éligibles. Pour des gestes ciblés : isolation des combles 1 500-3 500 €, remplacement chaudière fioul par PAC 14 000 €, fenêtres double vitrage 350-1 500 € par unité." },
  { question: "Faut-il être propriétaire pour bénéficier des aides ?", answer: "Oui pour MaPrimeRénov' (occupants ou bailleurs d'un logement de plus de 15 ans). Les locataires ne peuvent pas demander l'aide directement mais peuvent inciter leur bailleur à entreprendre les travaux, notamment pour les passoires thermiques (logements F et G interdits à la location depuis 2025 et 2028 respectivement). Les CEE sont accessibles aux propriétaires comme aux locataires sans condition de revenus." },
  { question: "Quelle aide pour une pompe à chaleur en 2026 ?", answer: "MaPrimeRénov' verse 5 000 € (Bleu très modeste), 4 000 € (Jaune modeste) ou 3 000 € (Violet intermédiaire) pour une PAC air/eau. Cumulable avec une prime CEE Coup de Pouce Chauffage de 4 000 à 5 000 € selon les revenus, et la TVA réduite à 5,5 %. Pour les ménages très modestes, le cumul peut atteindre 11 000 € sur un devis de 14 000 €, soit 80 % du coût couvert. L'artisan doit être certifié RGE QualiPAC." },
  { question: "Pourquoi l'isolation des murs n'est plus aidée en parcours par geste depuis 2026 ?", answer: "Depuis le 1er janvier 2026, l'isolation par l'intérieur (ITI) et par l'extérieur (ITE) des murs est sortie du parcours par geste de MaPrimeRénov'. Pour bénéficier d'une aide sur ces postes, il faut désormais passer par le parcours accompagné (rénovation d'ampleur ≥ 2 classes DPE). Cette évolution reflète l'orientation gouvernementale vers les rénovations globales plutôt que les gestes isolés. Les CEE Coup de Pouce Isolation murs restent disponibles." },
  { question: "Comment trouver un artisan RGE certifié ?", answer: "AfficheServices propose un filtre RGE granulaire sur ses pages catégorie : QualiPAC pour les pompes à chaleur, Qualibat 7141 pour l'isolation, Qualifelec pour l'électricité, QualiPV pour le photovoltaïque. Chaque artisan référencé passe 3 contrôles : SIRET via SIRENE, assurance RC Pro vérifiée, vérification d'identité KYC pour les comptes Pro et Enterprise. Sans certification RGE valide pour la prestation concernée, aucune aide n'est versée." },
  { question: "Que faire en cas de refus ou de litige ?", answer: "En cas de refus de l'Anah, recours gracieux possible auprès du préfet du département dans les 2 mois. Le motif fréquent (devis signé avant dépôt de la demande) est rédhibitoire. En cas de litige avec l'artisan, AfficheServices propose une médiation gratuite. Si elle échoue : Médiateur de la consommation (recours obligatoire avant tribunal), DGCCRF en cas de pratique commerciale trompeuse, ou Médiateur de l'énergie pour litiges spécifiques aux PAC." },
]
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import {
  Leaf, Flame, Shield, Calculator, ArrowRight,
  Thermometer, Home, Zap, Snowflake, Sun, DoorOpen,
  CheckCircle, TrendingDown, BadgeCheck,
} from 'lucide-react'

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'Rénovation énergétique 2026 — Aides, artisans RGE, prix' },
  { name: 'description', content: "Guide complet rénovation énergétique 2026 : MaPrimeRénov' (jusqu'à 90 %), CEE, éco-PTZ, artisans RGE certifiés. Méthodologie, exemples chiffrés, calcul d'aides instantané." },
  { name: 'robots', content: 'index, follow' },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/renovation-energetique' },
]

const AIDE_CARDS = [
  {
    icon: Flame,
    color: 'from-orange-500 to-red-500',
    iconBg: 'bg-orange-100 text-orange-600',
    title: 'MaPrimeRénov\'',
    desc: 'L\'aide principale de l\'État. Jusqu\'à 10 000 € pour une rénovation globale. Tous les propriétaires éligibles.',
    href: '/maprimenov',
    tag: 'Jusqu\'à 10 000 €',
  },
  {
    icon: Zap,
    color: 'from-blue-500 to-indigo-500',
    iconBg: 'bg-blue-100 text-blue-600',
    title: 'Primes CEE',
    desc: 'Primes des fournisseurs d\'énergie. Cumulables avec MaPrimeRénov\'. De 200 à 4 000 € selon les travaux.',
    href: '/cee',
    tag: 'Cumulable',
  },
  {
    icon: Calculator,
    color: 'from-emerald-500 to-teal-500',
    iconBg: 'bg-emerald-100 text-emerald-600',
    title: 'Éco-PTZ',
    desc: 'Prêt à taux zéro jusqu\'à 50 000 €. Sans condition de revenus. Remboursable sur 20 ans.',
    href: '/guide/eco-ptz',
    tag: 'Taux zéro',
  },
  {
    icon: BadgeCheck,
    color: 'from-violet-500 to-purple-500',
    iconBg: 'bg-violet-100 text-violet-600',
    title: 'Artisans RGE',
    desc: 'Trouvez un artisan certifié RGE près de chez vous. Obligatoire pour bénéficier des aides.',
    href: '/rge',
    tag: 'Obligatoire',
  },
]

const TRAVAUX = [
  { icon: Thermometer, title: 'Isolation thermique', desc: 'Combles, murs, sols — réduisez jusqu\'à 30 % de pertes', href: '/guide/isolation-combles', savings: '-30 % chauffage' },
  { icon: Snowflake, title: 'Pompe à chaleur', desc: 'Air/eau, géothermie — chauffez pour 3× moins cher', href: '/guide/pompe-a-chaleur', savings: '-65 % énergie' },
  { icon: DoorOpen, title: 'Fenêtres', desc: 'Double/triple vitrage — confort thermique et acoustique', href: '/guide/changer-fenetres', savings: '-15 % pertes' },
  { icon: Sun, title: 'Panneaux solaires', desc: 'Produisez votre propre électricité et revendez le surplus', href: '/prix/panneaux-solaires-paris', savings: '-50 % facture' },
  { icon: Home, title: 'Rénovation globale', desc: 'Gain de 2+ classes DPE — les aides les plus élevées', href: '/guide/maprimerenov-2026', savings: 'Bonus +10 000 €' },
  { icon: Flame, title: 'Chauffage bois', desc: 'Poêle à granulés, chaudière biomasse — énergie renouvelable', href: '/prix/chauffagiste-paris', savings: '-40 % énergie' },
]

export default function RenovationEnergetique() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Rénovation énergétique', url: 'https://www.afficheservices.fr/renovation-energetique' },
      ]} />
      <ArticleSchema
        title="Rénovation énergétique 2026 : aides, artisans RGE, méthodologie"
        description="Guide pillier rénovation énergétique 2026. MaPrimeRénov', CEE, éco-PTZ, artisans RGE, exemples chiffrés."
        url="https://www.afficheservices.fr/renovation-energetique"
      />
      <FAQSchema items={RE_FAQS} />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-900 via-green-800 to-teal-900 px-4 py-16 text-white sm:py-24">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -right-20 -top-20 h-96 w-96 rounded-full bg-emerald-400 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 h-96 w-96 rounded-full bg-teal-400 blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-5xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-emerald-200 backdrop-blur-sm">
            <Leaf className="h-3.5 w-3.5" /> Barèmes 2026 mis à jour
          </div>
          <h1 className="mt-5 text-3xl font-extrabold sm:text-5xl">
            Rénovation énergétique
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-emerald-100">
            Réduisez votre facture d'énergie, améliorez votre DPE et profitez des aides de l'État.
            Guide complet : aides, artisans certifiés, travaux prioritaires.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link to="/simulateur-aides" className="inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3.5 text-sm font-bold text-emerald-900 shadow-lg hover:bg-emerald-50 transition-all">
              <Calculator className="h-4 w-4" /> Calculer mes aides
            </Link>
            <Link to="/rge" className="inline-flex items-center gap-2 rounded-xl border border-white/30 bg-white/10 px-6 py-3.5 text-sm font-bold text-white backdrop-blur-sm hover:bg-white/20 transition-all">
              <BadgeCheck className="h-4 w-4" /> Trouver un artisan RGE
            </Link>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-4 pt-10 lg:px-6">
        <TrustStrip />
      </div>

      <div className="mx-auto max-w-4xl px-4 pt-6 lg:px-6">
        <InlineLeadCapture
          heading="Démarrez votre rénovation énergétique"
          subheading="Code postal → 3 artisans RGE pour MaPrimeRénov' + CEE"
        />
      </div>

      {/* Direct answer + Key Takeaways for AI search */}
      <section className="mx-auto max-w-4xl px-4 py-12 lg:px-6">
        <p className="text-base leading-relaxed text-slate-700">
          La rénovation énergétique en France en 2026 est financée par 4 dispositifs cumulables : <strong>MaPrimeRénov'</strong> (jusqu'à 90 % du coût pour les ménages très modestes),
          les <strong>Certificats d'Économies d'Énergie (CEE)</strong> versés par les fournisseurs d'énergie sans condition de revenus,
          l'<strong>éco-PTZ</strong> jusqu'à 50 000 € à taux zéro, et la <strong>TVA réduite à 5,5 %</strong> sur les travaux énergétiques.
          L'isolation des combles reste l'investissement le plus rentable, avec un retour sur investissement en 5 à 8 ans
          et 30 % d'économies de chauffage. Tous les travaux doivent être réalisés par un artisan certifié RGE pour ouvrir droit aux aides.
        </p>

        <div className="mt-8 rounded-2xl border-2 border-emerald-200 bg-emerald-50/40 p-6">
          <h2 className="text-base font-bold uppercase tracking-wider text-emerald-700">À retenir</h2>
          <ul className="mt-3 space-y-2 text-sm leading-relaxed text-slate-700">
            <li className="flex gap-2"><Leaf className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> 4 aides cumulables en 2026 : MaPrimeRénov' + CEE + éco-PTZ + TVA 5,5 %, qui peuvent couvrir 30 à 90 % du coût total selon les revenus.</li>
            <li className="flex gap-2"><Leaf className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> Ordre de priorité des travaux : isolation des combles d'abord (ROI 5-8 ans), puis chauffage (PAC), puis fenêtres, puis murs (uniquement via parcours accompagné depuis 2026).</li>
            <li className="flex gap-2"><Leaf className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> Artisan certifié RGE obligatoire pour la prestation : QualiPAC pour les pompes à chaleur, Qualibat pour l'isolation, Qualifelec pour l'électricité.</li>
            <li className="flex gap-2"><Leaf className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> Engagement CEE et dépôt MaPrimeRénov' <strong>AVANT signature du devis</strong> — toute signature préalable annule définitivement l'aide.</li>
            <li className="flex gap-2"><Leaf className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> Logements G interdits à la location depuis 2025, F en 2028 — les passoires thermiques perdent leur valeur locative.</li>
          </ul>
        </div>
      </section>

      {/* Methodology — order of priority */}
      <section className="mx-auto max-w-4xl px-4 py-8 lg:px-6">
        <h2 className="text-2xl font-bold text-slate-900">Par où commencer sa rénovation énergétique ?</h2>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">
          L'ordre de priorité des travaux ne dépend pas du « rendu visible » mais du retour sur investissement énergétique.
          Voici la séquence optimale validée par l'ADEME et l'Anah pour 90 % des logements français.
        </p>
        <ol className="mt-6 space-y-4 text-sm">
          <li className="rounded-xl border border-emerald-200 bg-emerald-50/30 p-5">
            <p className="font-bold text-emerald-900">1. Isolation des combles perdus (ROI 5-8 ans)</p>
            <p className="mt-1 text-slate-700">
              Investissement n°1 du logement : 30 % d'économies de chauffage pour 20-35 €/m². 30 cm de laine soufflée
              minimum (R ≥ 7 m²K/W). MaPrimeRénov' Bleu couvre souvent l'intégralité, CEE Coup de Pouce Isolation cumulable.
            </p>
          </li>
          <li className="rounded-xl border border-blue-200 bg-blue-50/30 p-5">
            <p className="font-bold text-blue-900">2. Système de chauffage (ROI 6-10 ans avec aides)</p>
            <p className="mt-1 text-slate-700">
              Remplacement chaudière fioul ou gaz par PAC air/eau (8-15 000 €) ou géothermique (15-25 000 €).
              Cumul MaPrimeRénov' + CEE Coup de Pouce Chauffage peut couvrir 80-90 % pour Bleu/Jaune.
            </p>
          </li>
          <li className="rounded-xl border border-amber-200 bg-amber-50/30 p-5">
            <p className="font-bold text-amber-900">3. Fenêtres double vitrage (ROI 12-18 ans)</p>
            <p className="mt-1 text-slate-700">
              Remplacer du simple vitrage : 350-1 500 € par fenêtre selon le matériau (PVC, alu, bois). MaPrimeRénov' 40-100 €/unité.
              Moins prioritaire en pur ROI mais essentiel pour le confort acoustique et thermique.
            </p>
          </li>
          <li className="rounded-xl border border-violet-200 bg-violet-50/30 p-5">
            <p className="font-bold text-violet-900">4. Isolation des murs — parcours accompagné uniquement depuis 2026</p>
            <p className="mt-1 text-slate-700">
              ITE (extérieur) 150-250 €/m² supprime tous les ponts thermiques mais nécessite un ravalement coordonné.
              ITI (intérieur) 50-100 €/m² réduit la surface habitable. Désormais éligible uniquement via le parcours accompagné
              MaPrimeRénov' (rénovation d'ampleur ≥ 2 classes DPE).
            </p>
          </li>
          <li className="rounded-xl border border-orange-200 bg-orange-50/30 p-5">
            <p className="font-bold text-orange-900">5. Ventilation double flux (ROI 7-12 ans)</p>
            <p className="mt-1 text-slate-700">
              VMC double flux (3 500-6 000 €) récupère 70-90 % de la chaleur de l'air vicié extrait. MaPrimeRénov' 1 500-2 500 €
              + CEE. Indispensable après isolation lourde pour éviter l'humidité.
            </p>
          </li>
        </ol>
      </section>

      {/* 3 worked examples */}
      <section className="mx-auto max-w-4xl px-4 py-8 lg:px-6">
        <h2 className="text-2xl font-bold text-slate-900">3 exemples de rénovation chiffrés</h2>
        <div className="mt-6 space-y-5">
          <div className="rounded-2xl border border-border bg-white p-6 shadow-xs">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Cas 1 : pavillon F → C, ménage Bleu</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-700">
              <strong>Brigitte et Pierre</strong>, retraités à Reims, RFR 24 000 € (Bleu très modeste). Maison de 1972, 95 m², classée F au DPE.
              Ils combinent : isolation des combles (2 800 €), remplacement chaudière fioul par PAC air/eau (14 000 €), VMC double flux (4 500 €).
              <strong>Total devis : 21 300 €.</strong> Aides cumulées : MaPrimeRénov' Bleu (5 000 € PAC + 2 000 € isolation + 2 500 € VMC = 9 500 €)
              + CEE Coup de Pouce (5 000 € PAC + 800 € isolation = 5 800 €) + Bonus sortie passoire (1 000 €) + Bonus BBC (500 €) = <strong>16 800 € d'aides</strong>.
              Reste à charge : 4 500 € sur éco-PTZ. ROI 4 ans avec 1 800 €/an d'économies.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-white p-6 shadow-xs">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Cas 2 : appartement T3, ménage Jaune</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-700">
              <strong>Mehdi et Anaïs</strong>, jeunes parents à Toulouse, RFR 38 000 € (Jaune modeste). Appartement T3 75 m² classé E, copropriété récente.
              Travaux : remplacement fenêtres (8 unités à 700 € = 5 600 €) + VMC simple (1 500 €).
              MaPrimeRénov' Jaune fenêtres 80 €/unité × 8 = <strong>640 €</strong>. CEE Coup de Pouce fenêtres : 800 €. TVA réduite 5,5 % au lieu de 20 %
              = économie supplémentaire 1 015 €. Total aides : <strong>2 455 €</strong> sur 7 100 € de devis. Confort thermique et acoustique amélioré, gain DPE 1 classe.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-white p-6 shadow-xs">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Cas 3 : rénovation globale parcours accompagné</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-700">
              <strong>Famille Dubois</strong>, 4 personnes à Lyon, RFR 52 000 € (Jaune modeste). Maison de 1985, 110 m², classée E. Objectif : atteindre la classe B
              (gain de 3 classes). Audit énergétique 800 €. Budget travaux : isolation combles + ITE murs + PAC air/eau + VMC double flux + fenêtres triple vitrage = <strong>48 000 € HT</strong>.
              MaPrimeRénov' parcours accompagné Jaune : 50 % de 40 000 € HT = <strong>20 000 €</strong>. CEE bonifiés 6 000 €. Bonus sortie passoire 800 €.
              Total aides 26 800 €. Reste à charge 21 200 €, finançable par éco-PTZ 50 000 € sur 20 ans à taux zéro. Économies de chauffage 2 200 €/an, ROI 9 ans.
            </p>
          </div>
        </div>
      </section>

      {/* Aide cards */}
      <section className="mx-auto max-w-6xl px-4 py-14 lg:px-6">
        <h2 className="text-center text-2xl font-bold text-slate-900">Les aides à la rénovation en 2026</h2>
        <p className="mt-2 text-center text-sm text-slate-500">Toutes cumulables entre elles pour minimiser votre reste à charge</p>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {AIDE_CARDS.map((aide) => (
            <Link
              key={aide.title}
              to={aide.href}
              className="group relative overflow-hidden rounded-2xl border border-border bg-white p-6 shadow-xs transition-all hover:shadow-lg hover:-translate-y-1"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${aide.color} opacity-0 transition-opacity group-hover:opacity-5`} />
              <div className="relative">
                <div className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${aide.iconBg}`}>
                  <aide.icon className="h-5 w-5" />
                </div>
                <span className="mt-3 inline-block rounded-full bg-emerald-50 px-2.5 py-0.5 text-[10px] font-bold text-emerald-700">{aide.tag}</span>
                <h3 className="mt-2 text-base font-bold text-slate-900">{aide.title}</h3>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{aide.desc}</p>
                <span className="mt-3 flex items-center gap-1 text-xs font-semibold text-primary group-hover:underline">
                  En savoir plus <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Travaux prioritaires */}
      <section className="border-t border-border bg-surface-secondary px-4 py-14">
        <div className="mx-auto max-w-6xl lg:px-6">
          <h2 className="text-center text-2xl font-bold text-slate-900">Travaux prioritaires</h2>
          <p className="mt-2 text-center text-sm text-slate-500">Classés par impact sur votre facture d'énergie</p>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {TRAVAUX.map((t) => (
              <Link
                key={t.title}
                to={t.href}
                className="group flex items-start gap-4 rounded-2xl border border-border bg-white p-5 shadow-xs transition-all hover:shadow-md hover:border-emerald-200"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 transition-transform group-hover:scale-110">
                  <t.icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-bold text-slate-900">{t.title}</h3>
                    <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">{t.savings}</span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{t.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* DPE section */}
      <section className="mx-auto max-w-4xl px-4 py-14 lg:px-6">
        <h2 className="text-center text-2xl font-bold text-slate-900">Comprendre le DPE</h2>
        <p className="mt-2 text-center text-sm text-slate-500">Le Diagnostic de Performance Énergétique classe votre logement de A à G</p>

        <div className="mt-8 space-y-2">
          {[
            { letter: 'A', range: '≤ 70 kWh/m²/an', color: 'bg-green-600', width: '10%', text: 'Excellent — maison passive ou BBC' },
            { letter: 'B', range: '71-110', color: 'bg-green-500', width: '20%', text: 'Très bon — neuf RT 2012' },
            { letter: 'C', range: '111-180', color: 'bg-lime-500', width: '35%', text: 'Bon — logement récent bien isolé' },
            { letter: 'D', range: '181-250', color: 'bg-yellow-400', width: '50%', text: 'Moyen — la majorité du parc français' },
            { letter: 'E', range: '251-330', color: 'bg-orange-400', width: '65%', text: 'Insuffisant — travaux recommandés' },
            { letter: 'F', range: '331-420', color: 'bg-orange-600', width: '80%', text: 'Passoire thermique — travaux urgents' },
            { letter: 'G', range: '> 420', color: 'bg-red-600', width: '100%', text: 'Interdit à la location depuis 2025' },
          ].map((dpe) => (
            <div key={dpe.letter} className="flex items-center gap-3">
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${dpe.color} text-sm font-bold text-white`}>
                {dpe.letter}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="h-3 overflow-hidden rounded-full bg-slate-100" style={{ width: dpe.width }}>
                    <div className={`h-full rounded-full ${dpe.color}`} style={{ width: '100%' }} />
                  </div>
                  <span className="shrink-0 text-xs font-medium text-slate-500">{dpe.range}</span>
                </div>
                <p className="mt-0.5 text-[11px] text-slate-400">{dpe.text}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <p className="font-semibold">Depuis janvier 2025 :</p>
          <p className="mt-1">Les logements classés G sont interdits à la location. En 2028, ce sera le tour des F. Rénover maintenant protège votre patrimoine.</p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="mx-auto max-w-4xl px-4 py-14 lg:px-6">
        <h2 className="text-2xl font-bold text-slate-900">Questions fréquentes — Rénovation énergétique 2026</h2>
        <div className="mt-6 space-y-4">
          {RE_FAQS.map((f, i) => (
            <details key={i} className="group rounded-xl border border-border bg-white p-4 shadow-xs">
              <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-slate-900 hover:text-primary">
                {f.question}
                <span className="text-slate-300 group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{f.answer}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border bg-gradient-to-r from-emerald-50 to-teal-50 px-4 py-14">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-2xl font-bold text-slate-900">Commencez votre rénovation énergétique</h2>
          <p className="mt-2 text-sm text-slate-500">Recevez jusqu'à 3 devis gratuits d'artisans RGE certifiés</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
            <Link to="/demande-devis" className="inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-4 text-sm font-bold text-white shadow-md hover:bg-primary-hover btn-press">
              Demander un devis gratuit <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/simulateur-aides" className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-3.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
              <Calculator className="h-4 w-4" /> Simuler mes aides
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1"><CheckCircle className="h-3.5 w-3.5 text-emerald-500" /> 100% gratuit</span>
            <span className="flex items-center gap-1"><Shield className="h-3.5 w-3.5 text-emerald-500" /> Artisans RGE vérifiés</span>
            <span className="flex items-center gap-1"><TrendingDown className="h-3.5 w-3.5 text-emerald-500" /> Économies garanties</span>
          </div>
        </div>
      </section>

      <FloatingCTA label="Démarrez votre rénovation" subtitle="3 artisans RGE · 100 % gratuit" whatsappMessage="Bonjour, je souhaite démarrer ma rénovation énergétique." />
      <Footer />
    </div>
  )
}
